import React from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiX, FiFilter, FiCheck, FiUsers, FiTarget } from 'react-icons/fi';
import { GAME_DATA, GLOBAL_OPTIONS } from '../data/mockData';

export default function DiscoveryFilters({ filters, setFilters, onClose }) {
  const toggleMultiFilter = (key, value) => {
    setFilters(prev => {
      const currentValues = prev[key] || [];
      const newValues = currentValues.includes(value) 
        ? currentValues.filter(v => v !== value) 
        : [...currentValues, value];
      return { ...prev, [key]: newValues };
    });
  };

  const updateGame = (game) => {
    setFilters(prev => ({
      ...prev,
      game,
      role: [],
      rank: []
    }));
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      game: 'All',
      role: [],
      rank: [],
      language: [],
      timezone: 'All',
      type: 'All',
      availability: 'All',
      platform: [],
      lookingForTeam: false
    });
  };

  const MultiSelectGroup = ({ label, values, options, onChange }) => (
    <div className="mb-6">
      <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest mb-3 px-1">
        {label} {values.length > 0 && <span className="text-brand-600 ml-1">({values.length})</span>}
      </label>
      <div className="flex flex-wrap gap-2">
        {options.map(opt => {
          const isSelected = values.includes(opt);
          return (
            <button
              key={opt}
              onClick={() => onChange(opt)}
              className={`px-3 py-1.5 rounded-xl text-[11px] font-bold transition-all border flex items-center gap-1.5 ${
                isSelected 
                  ? 'bg-brand-600 text-white border-brand-600 shadow-md shadow-brand-100 dark:shadow-brand-900/20' 
                  : 'bg-gray-50 dark:bg-dark-card text-gray-500 dark:text-gray-400 border-gray-100 dark:border-dark-border hover:bg-gray-100 dark:hover:bg-dark-border/50'
              }`}
            >
              {isSelected && <SafeIcon icon={FiCheck} className="w-3 h-3" />}
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="bg-white dark:bg-dark-surface h-full flex flex-col transition-colors">
      <div className="p-4 border-b border-gray-100 dark:border-dark-border flex justify-between items-center lg:hidden">
        <h2 className="font-black text-brand-950 dark:text-white flex items-center gap-2 uppercase text-sm tracking-tight">
          <SafeIcon icon={FiFilter} /> Advanced Filters
        </h2>
        <button onClick={onClose} className="p-2 text-gray-500 hover:bg-gray-50 dark:hover:bg-dark-card rounded-xl">
          <SafeIcon icon={FiX} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 custom-scrollbar">
        {/* Looking for Team Filter - High Visibility */}
        <div className="mb-8">
          <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest mb-3 px-1">Recruitment Status</label>
          <button
            onClick={() => setFilters(prev => ({ ...prev, lookingForTeam: !prev.lookingForTeam }))}
            className={`w-full p-4 rounded-2xl border-2 transition-all flex items-center justify-between group ${
              filters.lookingForTeam 
                ? 'bg-brand-600 border-brand-600 text-white shadow-lg shadow-brand-200 dark:shadow-brand-900/20' 
                : 'bg-white dark:bg-dark-card border-gray-100 dark:border-dark-border text-gray-500 hover:border-brand-200'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                filters.lookingForTeam ? 'bg-white/20' : 'bg-brand-50 dark:bg-dark-surface'
              }`}>
                <SafeIcon icon={FiUsers} className={filters.lookingForTeam ? 'text-white' : 'text-brand-600'} />
              </div>
              <div className="text-left">
                <p className={`text-xs font-black uppercase tracking-tight ${filters.lookingForTeam ? 'text-white' : 'text-brand-950 dark:text-white'}`}>Looking for Team</p>
                <p className={`text-[9px] font-bold uppercase tracking-tight ${filters.lookingForTeam ? 'text-white/70' : 'text-gray-400'}`}>Serious Recruitment</p>
              </div>
            </div>
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
              filters.lookingForTeam ? 'border-white bg-white' : 'border-gray-200 dark:border-dark-border'
            }`}>
              {filters.lookingForTeam && <SafeIcon icon={FiCheck} className="text-brand-600 w-3 h-3" />}
            </div>
          </button>
        </div>

        <div className="mb-6">
          <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest mb-3 px-1">Main Game</label>
          <div className="relative">
            <select 
              value={filters.game} 
              onChange={(e) => updateGame(e.target.value)}
              className="w-full bg-gray-50 dark:bg-dark-card border border-gray-100 dark:border-dark-border text-gray-700 dark:text-gray-200 text-sm font-bold rounded-2xl p-4 outline-none focus:ring-4 focus:ring-brand-500/10 transition-all appearance-none"
            >
              <option value="All">All Games</option>
              {Object.keys(GAME_DATA).map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
              <SafeIcon icon={FiTarget} className="w-4 h-4" />
            </div>
          </div>
        </div>

        {filters.game !== 'All' && (
          <>
            <MultiSelectGroup 
              label="Roles" 
              values={filters.role} 
              options={GAME_DATA[filters.game].roles} 
              onChange={(v) => toggleMultiFilter('role', v)} 
            />
            <MultiSelectGroup 
              label="Level (Rank)" 
              values={filters.rank} 
              options={GAME_DATA[filters.game].ranks} 
              onChange={(v) => toggleMultiFilter('rank', v)} 
            />
          </>
        )}

        <MultiSelectGroup 
          label="Languages" 
          values={filters.language} 
          options={GLOBAL_OPTIONS.languages} 
          onChange={(v) => toggleMultiFilter('language', v)} 
        />

        <MultiSelectGroup 
          label="Platforms" 
          values={filters.platform} 
          options={GLOBAL_OPTIONS.platforms} 
          onChange={(v) => toggleMultiFilter('platform', v)} 
        />

        <div className="mb-6">
          <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest mb-3 px-1">Approach</label>
          <select 
            value={filters.type} 
            onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
            className="w-full bg-gray-50 dark:bg-dark-card border border-gray-100 dark:border-dark-border text-gray-700 dark:text-gray-200 text-sm font-bold rounded-2xl p-4 outline-none appearance-none"
          >
            <option value="All">Any Style</option>
            {GLOBAL_OPTIONS.playerTypes.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
      </div>

      <div className="p-5 border-t border-gray-100 dark:border-dark-border bg-gray-50/30 dark:bg-dark-card/30">
        <button 
          onClick={resetFilters}
          className="w-full py-3 text-[11px] font-black text-gray-400 dark:text-gray-500 hover:text-brand-600 dark:hover:text-brand-400 uppercase tracking-[0.2em] transition-colors"
        >
          Reset Filters
        </button>
      </div>
    </div>
  );
}